import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useNavigate } from 'react-router';

import { cn } from '@rocco/utils/cn';
import { ArrowLeft } from 'lucide-react';

import { RoccoLink } from '@rocco/components/link';
import { getImageAttrs } from '~/modules/images';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { DetailLogoBadge } from '~/ui-rocco/pages/detail-shared/DetailLogoBadge';
import { SectionLabel } from '~/ui-rocco/pages/home-page/components/SectionLabel';
import { CONTENT_CAP_PADDED, OVERLAY_CAP } from './large-screen';
import { BANNER_PARALLAX_SCALE, BANNER_SCRIM } from './banner-motion';
import {
    BannerExposureDefs,
    bannerExposureStyle,
} from '~/ui-rocco/pages/detail-shared/BannerExposure';

const CLAMP_STYLE = {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical' as const,
    WebkitLineClamp: 2,
    overflow: 'hidden',
};

export interface DetailHeroProps {
    /** Parenthesised eyebrow, e.g. "(Cult Design)". */
    eyebrow?: string;
    /** When set, the eyebrow links here (e.g. the professional's profile). */
    eyebrowLink?: string;
    title: string;
    /**
     * Single full-bleed background image (project). For the professional hero,
     * pass {@link backgroundImages} instead to get a cross-fade carousel. When
     * neither is set, renders the text hero offset to the right of the
     * unified-header logomark (product).
     */
    backgroundImage?: string;
    /** Multiple background images — cross-fades between them (professional hero). */
    backgroundImages?: string[];
    /**
     * Full-bleed background video, painted over {@link backgroundImage} once
     * hydrated. The still stays the SSR'd layer and the video's poster, so the
     * hero has something to show on a cold cache and for readers who ask for
     * reduced motion.
     */
    backgroundVideo?: string;
    /** Logo badge for the image-hero variant. `link` makes the badge a profile link. */
    logo?: { src: string; background?: string | null; link?: string };
    /**
     * Image-hero geometry overrides (merged over the defaults via `cn`, so a
     * page only passes the dimensions that differ). Used where a page's hero is
     * a different height or sits the title elsewhere than the default
     * bottom-left treatment.
     */
    aspectClassName?: string;
    contentClassName?: string;
    titleClassName?: string;
    /**
     * A band laid across the banner above the title — a rule with a row of
     * small facts under it. Its own layer rather than part of the title block,
     * because it belongs to the banner's midline, not to the name that sits
     * near its foot.
     */
    metaBand?: ReactNode;
    /**
     * Move the banner and the words over it at different rates as the page
     * scrolls. Opt-in: it belongs to the surfaces where the banner is the
     * whole first screen.
     */
    parallax?: boolean;
    /**
     * An opening sequence laid over the banner. Rendered inside the section's
     * own box so it measures exactly the banner — a layer fixed to the
     * viewport would finish at whatever height the window happened to be.
     */
    intro?: ReactNode;
    /**
     * Pull the banner's highlights down so white chrome reads over it. Opt-in:
     * a banner that is already dark needs nothing.
     */
    tameHighlights?: boolean;
    /** Override the logo badge geometry (e.g. its right offset / alignment). */
    logoClassName?: string;
    /**
     * Extra overlay pinned inside the same centred container as the logo badge,
     * for content that has to sit beside it and straddle the banner's edge with
     * it (the professional hero's brand-family marks). The caller positions it —
     * it owns the badge geometry via {@link logoClassName} too.
     */
    logoAside?: ReactNode;
    /**
     * Replace the bottom legibility scrim entirely. Pages whose hero sits above
     * a dark section pass a fade that reaches that section's colour at the very
     * bottom so the hero blends seamlessly into it (e.g. the project page).
     */
    scrimClassName?: string;
    /**
     * A press on the photograph itself. The banner becomes a zoom target for
     * a page whose viewer can show it; the title, badge and cue on top keep
     * their own clicks.
     */
    onBackgroundClick?: () => void;
    /**
     * Client-toggled darkening overlay (the banner's Overlay setting in the
     * BM). Renders a uniform black/20 wash over the whole image, under the
     * legibility scrims.
     */
    overlay?: boolean;
    /**
     * Optional second scrim over the hero's top edge — a subtle fade that
     * keeps the transparent top nav legible over bright imagery. Rendered
     * only when provided.
     */
    topScrimClassName?: string;
    /**
     * A flat wash over the whole banner, under the legibility scrims. Distinct
     * from `overlay`, which is the client's own darkening toggle, and from the
     * scrims, which are gradients tuned to one edge: this is a tint that
     * settles the whole photograph towards the page it sits on.
     */
    tintClassName?: string;
    /**
     * View-transition name claim for the clipped banner box, so a tile can
     * morph into it (`useTileHeroViewTransitionStyle`). Applied to
     * the background div rather than the section: the overlaid title and logo
     * badge then ride the root cross-fade instead of flying inside the box.
     */
    heroStyle?: CSSProperties;
    /**
     * The already-decoded tile rendition the user clicked, painted under the
     * banner while the full-size rendition loads — a cold cache shows the
     * same pixels sharpening instead of the dark placeholder slab.
     */
    heroUnderlaySrc?: string | null;
    /**
     * Text-hero variant only: renders the title at the compact scale so the
     * content below (the product gallery) sits higher in the fold.
     */
    compact?: boolean;
    /**
     * Id for an empty, right-aligned box on the title's row. A section that
     * owns CTA state (the product's purchase section) portals a copy of its
     * actions in here, so the hero can show them beside the title without that
     * state being lifted out of the section that manages it. Omit and no box
     * is rendered.
     */
    titleActionsSlotId?: string;
    /** The hero's ground, for a page whose background is not white. */
    className?: string;
    /**
     * Show a "Back" control to the left of the title, under the header's
     * logomark. Uses history rather than a fixed href — the reader arrived from
     * a category, a search or a tile, and Back should return them there.
     */
    showBackButton?: boolean;
    /**
     * Where Back goes when there is no in-app history to return to — a product
     * opened from search, a shared link or a new tab. Without it the control
     * would be a no-op exactly when the reader most needs a way out.
     */
    backFallbackHref?: string;
    /** Rendered directly under the title, inside the same overlaid block. */
    belowTitle?: ReactNode;
    /**
     * A scroll affordance, held at the foot of the WINDOW rather than of the
     * banner — on a banner taller than the viewport the two are a long way
     * apart, and the point of the cue is that it is the last thing in view.
     */
    scrollCue?: ReactNode;
    /**
     * A small rendition of the first banner painted as the base layer on
     * every load (not just transition arrivals), so the hero shows the image
     * near-instantly and the full-size rendition sharpens over it.
     */
    heroBaseSrc?: string | null;
}

/** Default bottom-up scrim — backs the overlaid title over bright banners. */
const DEFAULT_SCRIM = BANNER_SCRIM;

/**
 * Full-bleed background that cross-fades between images (professional banner
 * carousel). A single image renders statically; reduced-motion holds the first
 * frame. Shared with the mobile social-profile hero.
 */
/**
 * The hero fills the window, and it is the LCP image on every page that has
 * one — so it resolves through the CDN loader at the same width the route's
 * own preload asks for.
 *
 * Not cosmetic. React's server renderer preloads every `<img>` it renders that
 * is not `loading="lazy"`, keyed on `srcSet` + `sizes` when there is one and
 * on `src` when there is not. A bare `src` here meant that key was the raw
 * image-service URL — the photograph at its authored size, 2.3MB on a project
 * banner — while the route preloaded the 1820-wide derivative it actually
 * wants. Two keys, so two downloads of the same picture, and the larger one
 * discarded. Keeping the two in step is what collapses them back into one.
 */
const HERO_SIZES = getImagesSizes(1820);

export const HeroCrossfade = ({ images }: { images: string[] }) => {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        if (images.length < 2) return;
        const reduce = window.matchMedia?.(
            '(prefers-reduced-motion: reduce)',
        ).matches;
        if (reduce) return;
        const id = setInterval(
            () => setIndex((i) => (i + 1) % images.length),
            5000,
        );
        return () => clearInterval(id);
    }, [images.length]);

    return (
        <>
            {images.map((src, i) => (
                <img
                    key={`${src}-${i}`}
                    {...getImageAttrs(src, { sizes: HERO_SIZES })}
                    alt=""
                    aria-hidden="true"
                    loading={i === 0 ? 'eager' : 'lazy'}
                    className="absolute inset-0 size-full object-cover transition-opacity duration-1000 ease-in-out"
                    style={{ opacity: i === index ? 1 : 0 }}
                />
            ))}
        </>
    );
};

/**
 * Background video layer for the image hero.
 *
 * Mounts only after hydration: the still underneath is what server-renders and
 * carries the hero's LCP, so the video is never on the critical path, and a
 * reader who asks for reduced motion simply keeps the still. Muted + inline are
 * what let it autoplay at all — browsers block a hero video with sound.
 */
const HeroBackgroundVideo = ({
    src,
    poster,
}: {
    src: string;
    poster?: string;
}) => {
    const [play, setPlay] = useState(false);

    useEffect(() => {
        const reduce = window.matchMedia?.(
            '(prefers-reduced-motion: reduce)',
        ).matches;
        if (!reduce) setPlay(true);
    }, []);

    if (!play) return null;

    return (
        <video
            src={src}
            poster={poster}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-hidden="true"
            tabIndex={-1}
            className="absolute inset-0 size-full object-cover"
        />
    );
};

/**
 * Shared detail-page hero.
 *
 * - **Text** (product): brand eyebrow + the large grotesk title, offset to the
 *   right of the header logomark and lifted so the eyebrow lines up with the
 *   bottom of the "A". The title clamps to two lines and expands on click when
 *   it overflows (long-title edge case).
 * - **Image** (project / professional): a full-bleed background (single image,
 *   or a cross-fade carousel via {@link backgroundImages}) with the eyebrow +
 *   title overlaid bottom-left and an optional logo badge top-right.
 */
export const DetailHero = ({
    eyebrow,
    eyebrowLink,
    title,
    backgroundImage,
    backgroundImages,
    backgroundVideo,
    logo,
    aspectClassName,
    contentClassName,
    titleClassName,
    metaBand,
    parallax = false,
    intro,
    tameHighlights = false,
    logoClassName,
    logoAside,
    scrimClassName,
    onBackgroundClick,
    topScrimClassName,
    tintClassName,
    overlay = false,
    heroStyle,
    heroUnderlaySrc,
    heroBaseSrc,
    belowTitle,
    scrollCue,
    compact = false,
    titleActionsSlotId,
    className,
    showBackButton = false,
    backFallbackHref = '/',
}: DetailHeroProps) => {
    const images =
        backgroundImages ?? (backgroundImage ? [backgroundImage] : []);
    const isImageHero = images.length > 0;

    const navigate = useNavigate();
    /**
     * Back goes through the router rather than `history.back()`, so it is a
     * real POP — that is what the browse grid's scroll restore listens for,
     * and the reader returns to the row they left rather than the top.
     *
     * React Router numbers its history entries; index 0 means this page is the
     * first in the tab (opened from search, a shared link, a new tab), so
     * there is nothing behind it and stepping back would land on a blank
     * page. Read at click time rather than render, since the index moves as
     * the reader navigates.
     */
    /**
     * Two layers, two speeds.
     *
     * Depth on a flat screen comes from things at different distances moving
     * by different amounts. The photograph lags the scroll — it travels at a
     * third of the page's rate, so it reads as sitting behind the window
     * rather than taped to it — while the name and the rule over it run
     * slightly ahead and fade as they leave. The image is scaled past its box
     * so that lag never drags an edge into view.
     *
     * Written straight to the nodes inside a rAF rather than held in state:
     * this runs on every scroll frame, and a re-render per frame would cost
     * far more than the paint.
     */
    /**
     * The scroll cue has said its piece the moment the reader scrolls, so it
     * goes. Held as a boolean rather than a scroll-linked opacity: it only
     * ever crosses once, and a value written every frame would re-render the
     * whole hero for it.
     */
    const [cueSpent, setCueSpent] = useState(false);
    const hasScrollCue = Boolean(scrollCue);
    useEffect(() => {
        if (!hasScrollCue) return;
        const onScroll = () => setCueSpent(window.scrollY > 80);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [hasScrollCue]);

    const mediaRef = useRef<HTMLDivElement>(null);
    const foregroundRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!parallax) return;
        const media = mediaRef.current;
        const foreground = foregroundRef.current;
        if (!media && !foreground) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        let frame = 0;
        const apply = () => {
            frame = 0;
            const travelled = window.scrollY;
            const height = window.innerHeight || 1;
            // Past a screen and a half the banner is long gone; holding the
            // numbers there keeps the transform from growing without end.
            const capped = Math.min(travelled, height * 1.5);
            if (media) {
                media.style.transform = `translate3d(0, ${capped * 0.34}px, 0) scale(${BANNER_PARALLAX_SCALE})`;
            }
            if (foreground) {
                foreground.style.transform = `translate3d(0, ${capped * -0.12}px, 0)`;
                foreground.style.opacity = String(
                    Math.max(0, 1 - travelled / (height * 0.75)),
                );
            }
        };
        const onScroll = () => {
            if (frame) return;
            frame = requestAnimationFrame(apply);
        };

        apply();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            if (frame) cancelAnimationFrame(frame);
        };
    }, [parallax]);

    const handleBack = () => {
        const historyIndex =
            typeof window === 'undefined'
                ? 0
                : ((window.history.state as { idx?: number } | null)?.idx ?? 0);
        if (historyIndex > 0) {
            navigate(-1);
            return;
        }
        navigate(backFallbackHref);
    };

    const renderEyebrow = (colorClass: string) => {
        if (!eyebrow) return null;
        const label = (
            <SectionLabel className={colorClass}>{eyebrow}</SectionLabel>
        );
        return eyebrowLink ? (
            <RoccoLink to={eyebrowLink} className="inline-block w-fit">
                {label}
            </RoccoLink>
        ) : (
            label
        );
    };
    const titleRef = useRef<HTMLHeadingElement>(null);
    const [expanded, setExpanded] = useState(false);
    const [overflows, setOverflows] = useState(false);

    const expandedRef = useRef(false);
    useEffect(() => {
        expandedRef.current = expanded;
    }, [expanded]);

    useEffect(() => {
        const el = titleRef.current;
        if (!el || isImageHero) return;
        const measure = () => {
            if (expandedRef.current) return;
            const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 1;
            setOverflows(Math.round(el.scrollHeight / lineHeight) > 2);
        };
        measure();
        document.fonts?.ready.then(measure).catch(() => {});
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [title, isImageHero]);

    if (isImageHero) {
        return (
            <section
                // Named so the hero is a real `region` landmark — on the
                // professional pages it mounts in the persistent layout,
                // outside the tab's `<main>`.
                aria-label={title}
                className={cn(
                    // md:max-h holds the banner at its 1920 design height on
                    // wider screens (the aspect box would keep growing) — the
                    // background crops wider via object-cover instead. Pages
                    // overriding the aspect pass a matching max-h.
                    // The banner is its own dark ground (image + gradient)
                    // whatever the page around it, so its controls take the
                    // off-white ring.
                    'relative w-full text-white focus-ring-on-dark max-md:aspect-[414/520] md:aspect-[1920/1373] md:max-h-[85.8125rem]',
                    aspectClassName,
                )}
            >
                {tameHighlights ? <BannerExposureDefs /> : null}
                {/* Clipped background — cross-fade carousel + bottom gradient.

                    The exposure sits on the clip, not on the moving layer
                    inside it: the scrims are the page's own chrome and should
                    not be graded with the photograph. */}
                <div
                    className="bg-neutral-900 absolute inset-0 overflow-hidden"
                    style={
                        tameHighlights
                            ? { ...heroStyle, ...bannerExposureStyle }
                            : heroStyle
                    }
                >
                    {/* The parallax moves this, not the box around it. A
                        transform on the clipping element scales the clip
                        itself, so the banner grew 14% past the section and the
                        whole page gained a horizontal scrollbar. Inside the
                        clip, the same transform is contained. The scrims stay
                        outside it — they are anchored to the banner's edges and
                        must not travel with the picture. */}
                    <div ref={mediaRef} className="absolute inset-0">
                        {heroBaseSrc ? (
                            <img
                                src={heroBaseSrc}
                                alt=""
                                aria-hidden="true"
                                className="absolute inset-0 size-full object-cover"
                            />
                        ) : null}
                        {heroUnderlaySrc ? (
                            <img
                                src={heroUnderlaySrc}
                                alt=""
                                aria-hidden="true"
                                className="absolute inset-0 size-full object-cover"
                            />
                        ) : null}
                        <HeroCrossfade images={images} />
                        {backgroundVideo ? (
                            <HeroBackgroundVideo
                                src={backgroundVideo}
                                poster={images[0]}
                            />
                        ) : null}
                    </div>
                    {/* Client-toggled darkening wash over the whole image,
                        under the legibility scrims. */}
                    {overlay ? (
                        <div className="absolute inset-0 bg-black/20" />
                    ) : null}
                    {tintClassName ? <div className={tintClassName} /> : null}
                    {/* Bottom legibility scrim; pages above a dark section pass
                        a scrimClassName that fades to that colour so the hero's
                        lower edge blends seamlessly into it. */}
                    <div className={scrimClassName ?? DEFAULT_SCRIM} />
                    {topScrimClassName ? (
                        <div className={topScrimClassName} />
                    ) : null}
                </div>
                {onBackgroundClick ? (
                    // Over the photograph and under everything drawn on it, so
                    // the picture opens and the title's own links still work.
                    <button
                        type="button"
                        aria-label="View image full size"
                        onClick={onBackgroundClick}
                        className="absolute inset-0 cursor-zoom-in border-0 bg-transparent p-0"
                    />
                ) : null}
                {/* The opening sequence, if the page has one. A direct child
                    of the section rather than of the centred container below:
                    `inset-0` has to measure the banner itself, and inside that
                    container it measured a max-width box and collapsed. */}
                {intro}
                {scrollCue ? (
                    <div
                        className={cn(
                            'pointer-events-none absolute inset-x-0 top-[calc(100vh-5.5rem)] z-30 flex justify-center max-md:hidden',
                            'transition-opacity duration-500 ease-out motion-reduce:transition-none',
                            cueSpent ? 'opacity-0' : 'opacity-100',
                        )}
                    >
                        {scrollCue}
                    </div>
                ) : null}
                {/* Overlay content anchors to the centred 1920 container, not
                    the full-bleed banner, so the title and badge keep their
                    design offsets on wider screens. */}
                <div
                    className={cn(
                        OVERLAY_CAP,
                        // Lets a press on the photograph reach the zoom target
                        // beneath: the container spans the whole banner, so
                        // it takes no pointer events of its own and gives
                        // them back to the title, badge and aside it holds.
                        onBackgroundClick &&
                            'pointer-events-none [&>*]:pointer-events-auto',
                    )}
                >
                    {logoAside}
                    {/* Brand badge straddles the bottom edge of the banner. */}
                    {logo ? (
                        logo.link ? (
                            <RoccoLink
                                to={logo.link}
                                className={cn(
                                    'detail-hero-logo-vt absolute z-10 block size-16 max-md:right-6 max-md:bottom-6 md:right-[7.68rem] md:-bottom-[5.4rem] md:size-[10.8rem]',
                                    logoClassName,
                                )}
                            >
                                <DetailLogoBadge
                                    src={logo.src}
                                    alt={`${title} logo`}
                                    background={logo.background}
                                    className="size-full"
                                />
                            </RoccoLink>
                        ) : (
                            <DetailLogoBadge
                                src={logo.src}
                                alt={`${title} logo`}
                                background={logo.background}
                                className={cn(
                                    'detail-hero-logo-vt absolute z-10 size-16 max-md:right-6 max-md:bottom-6 md:right-[7.68rem] md:-bottom-[5.4rem] md:size-[10.8rem]',
                                    logoClassName,
                                )}
                            />
                        )
                    ) : null}
                    {/* Measured from the top, not as a share of the banner: it
                        hangs off the header's logomark, which is itself
                        positioned in absolute units. 33rem puts the rule about
                        150px under the mark's foot — a little less than the
                        mark's own 166px height, so the two read as one group. */}
                    {metaBand ? (
                        <div
                            ref={foregroundRef}
                            className="absolute inset-x-0 z-10 will-change-transform max-md:hidden md:top-[33rem] md:px-[3rem]"
                        >
                            {metaBand}
                        </div>
                    ) : null}
                    {/* Title — anchored 52% down (Figma 714/1372.789) and
                        bottom-left on mobile. Pages may override the geometry
                        via the *ClassName props. */}
                    <div
                        className={cn(
                            'detail-hero-title-vt absolute max-md:right-6 max-md:bottom-6 max-md:left-6 md:max-w-[90rem]',
                            'md:top-[52%] md:left-[3.55rem]',
                            contentClassName,
                        )}
                    >
                        {renderEyebrow(
                            'text-white/80 transition-colors hover:text-white',
                        )}
                        <h1
                            className={cn(
                                'm-0 font-sans font-normal text-[#e5e5e5] max-md:mt-2 max-md:text-[2.3287rem] max-md:leading-[1.02] md:mt-[0.9rem] md:text-[7.5rem] md:leading-[1.08] md:tracking-[-0.3rem]',
                                titleClassName,
                            )}
                        >
                            {title}
                        </h1>
                        {belowTitle}
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section
            className={cn(
                'bg-white max-md:px-6 max-md:pt-8 md:px-[3rem] md:pt-[0.6rem]',
                className,
            )}
        >
            <div className={CONTENT_CAP_PADDED}>
                <div className="md:relative md:-top-[2.4rem] md:z-[130] md:-mb-[2.4rem] md:ml-[28.8rem]">
                    {/* items-end so the actions sit on the title's last line
                        rather than centring against the whole eyebrow+title
                        stack. min-w-0 lets a long title wrap instead of
                        squeezing the actions. */}
                    <div className="flex md:items-end md:justify-between md:gap-10">
                        <div className="min-w-0">
                            {/* Eyebrow shares the title ink (Figma product title 2354:58896),
                    not a separate grey — and lightens on hover, since as a link
                    to the professional it needs a state the title does not. */}
                            {renderEyebrow(
                                'text-[#343434] transition-colors hover:text-[#8f8f8f]',
                            )}
                            {/* Wrapped so Back can centre on the title rather
                                than on the eyebrow+title stack. */}
                            <div className="relative">
                                {showBackButton ? (
                                    <button
                                        type="button"
                                        onClick={handleBack}
                                        // Pulled back out of the title's 28.8rem
                                        // indent so it sits under the header's
                                        // logomark, and centred on the title
                                        // beside it. The CTA's type without the
                                        // CTA's pill.
                                        className="absolute top-[calc(50%+0.3125rem)] left-[-28.8rem] hidden -translate-y-1/2 items-center gap-[0.375rem] font-sans text-base leading-[1.3] font-medium tracking-[0.02em] whitespace-nowrap text-[#232323] transition-colors hover:text-[#8f8f8f] md:inline-flex"
                                    >
                                        <ArrowLeft
                                            className="size-[1.125rem] shrink-0"
                                            strokeWidth={2}
                                            aria-hidden="true"
                                        />
                                        Back
                                    </button>
                                ) : null}
                                <h1
                                    ref={titleRef}
                                    onClick={
                                        overflows
                                            ? () => setExpanded((v) => !v)
                                            : undefined
                                    }
                                    style={
                                        overflows && !expanded
                                            ? CLAMP_STYLE
                                            : undefined
                                    }
                                    className={cn(
                                        // Title: Figma product hero (node 2354:58896) — 100px
                                        // (6.25rem @1920), #343434, tracking -2px.
                                        'm-0 font-sans font-normal text-[#343434] max-md:mt-3 md:mt-[0.9rem] md:max-w-[81rem] md:tracking-[-0.125rem]',
                                        overflows && 'cursor-pointer',
                                        expanded || compact
                                            ? 'max-md:text-[1.5525rem] max-md:leading-[1.1] md:text-[3.9rem] md:leading-[1.08]'
                                            : 'max-md:text-[2.07rem] max-md:leading-[1.06] md:text-[6.25rem] md:leading-[1.0]',
                                    )}
                                >
                                    {title}
                                </h1>
                            </div>
                        </div>
                        {titleActionsSlotId ? (
                            <div
                                id={titleActionsSlotId}
                                className="shrink-0 pb-[0.45rem] max-md:hidden"
                            />
                        ) : null}
                    </div>
                </div>
            </div>
        </section>
    );
};
